/**
 * AdminSalesChannelMix — "National Channel Mix" card. A stacked
 * share-of-spend bar plus a per-platform legend with weekly spend and
 * share. Platform colors cycle through the dashboards token palette.
 */
import type { AdminSalesDto } from './adminApi';

/** Token-based color per platform slot (no inline hex per CI rules). */
const PALETTE = ['var(--nb-primary)', 'var(--nb-info)', 'var(--nb-leaf)', 'var(--nb-warn)'];

function colorFor(index: number): string {
  return PALETTE[index % PALETTE.length];
}

function fmtSpend(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

function ShareStack({ mix }: { mix: NonNullable<AdminSalesDto['channelMix']> }) {
  return (
    <div
      style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}
    >
      {mix.map((c, i) => (
        <div
          key={c.platform}
          title={`${c.platform} · ${c.sharePct}%`}
          style={{ width: `${c.sharePct}%`, background: colorFor(i) }}
        />
      ))}
    </div>
  );
}

function ChannelLegend({ mix }: { mix: NonNullable<AdminSalesDto['channelMix']> }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {mix.map((c, i) => (
        <div key={c.platform} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <i style={{ width: 9, height: 9, borderRadius: 3, background: colorFor(i) }} />
          <span style={{ flex: 1, fontSize: 11.5 }}>{c.platform}</span>
          <span className="nb-tabular nb-mono" style={{ fontSize: 11, fontWeight: 600 }}>
            {fmtSpend(c.spendCents)}
          </span>
          <span
            className="nb-tabular nb-mono"
            style={{ fontSize: 10.5, color: 'var(--nb-subtle)', width: 32, textAlign: 'right' }}
          >
            {c.sharePct}%
          </span>
        </div>
      ))}
    </div>
  );
}

export function AdminSalesChannelMix({ mix }: { mix: AdminSalesDto['channelMix'] }) {
  const empty = mix === null || mix.length === 0;
  return (
    <section className="nb-card" style={{ flex: '0 0 auto' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={{ fontSize: 13, letterSpacing: '.04em', margin: 0 }}>
          National Channel Mix
        </h3>
        <span className="nb-eyebrow">this week</span>
      </div>
      {empty ? (
        <div style={{ fontSize: 12, color: 'var(--nb-subtle)' }} role="note">
          Channel mix unavailable.
        </div>
      ) : (
        <>
          <ShareStack mix={mix} />
          <ChannelLegend mix={mix} />
        </>
      )}
    </section>
  );
}
